// Provides sanitising and conversion helpers for the email rich text editors and previews.
const HTML_ENTITY_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function normalizeRichTextColor(value: string | null | undefined): string | null {
  const cleaned = (value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(cleaned) ? cleaned.toLowerCase() : null;
}

export function sanitizeRichTextFontFamily(value: string | null | undefined): string | null {
  const cleaned = (value || '').replace(/[^a-zA-Z0-9,\- "'.]/g, '').trim();
  return cleaned || null;
}

export function normalizeFontFamilyChoice(value: string): string {
  return value.replace(/['"]/g, '').replace(/\s*,\s*/g, ', ').trim();
}

export function sanitizeRichTextHref(value: string | null | undefined): string | null {
  const cleaned = (value || '').trim();
  if (!cleaned) return null;
  const lower = cleaned.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('mailto:') || lower.startsWith('tel:')) {
    return cleaned;
  }
  return null;
}

export function sanitizeRichTextHtml(input: string): string {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    const paragraphs = trimmed
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`);
    return paragraphs.join('');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${trimmed}</div>`, 'text/html');

  const serializeNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const children = Array.from(element.childNodes).map(serializeNode).join('');

    if (tag === 'script' || tag === 'style') return '';
    if (tag === 'br') return '<br />';
    if (tag === 'strong' || tag === 'b') return `<strong>${children}</strong>`;
    if (tag === 'em' || tag === 'i') return `<em>${children}</em>`;
    if (tag === 'u') return `<span style="text-decoration:underline;">${children}</span>`;
    if (tag === 'p' || tag === 'div') return `<p>${children}</p>`;
    if (tag === 'ul' || tag === 'ol') return `<${tag}>${children}</${tag}>`;
    if (tag === 'li') return `<li>${children}</li>`;
    if (tag === 'a') {
      const href = sanitizeRichTextHref(element.getAttribute('href'));
      return href ? `<a href="${escapeHtml(href)}">${children}</a>` : children;
    }
    if (tag === 'span' || tag === 'font') return children;

    return children;
  };

  const bodyChildren = Array.from(doc.body.firstChild?.childNodes || []);
  const serialized = bodyChildren.map(serializeNode).join('');

  return serialized || `<p>${escapeHtml(trimmed)}</p>`;
}

function replacePreviewTokens(value: string, sampleName: string): string {
  const effectiveName = (sampleName || '').trim() || 'there';
  return value
    .replace(/\[Name\]/gi, effectiveName)
    .replace(/\{\{\s*first_name\s*\|\s*fallback:\s*"([^"]*)"\s*\}\}/gi, (_match, fallback) => effectiveName || fallback || 'there')
    .replace(/\{\{\s*name\s*\|\s*fallback:\s*"([^"]*)"\s*\}\}/gi, (_match, fallback) => effectiveName || fallback || 'there')
    .replace(/\{\{\s*first_name\s*\}\}/gi, effectiveName)
    .replace(/\{\{\s*name\s*\}\}/gi, effectiveName);
}

function trimLastTextBlockMargin(html: string): string {
  const lastParagraphMargin = html.lastIndexOf('margin:0 0 16px;');
  const lastListMargin = html.lastIndexOf('margin:0 0 16px 24px;');

  if (lastParagraphMargin === -1 && lastListMargin === -1) return html;
  if (lastParagraphMargin > lastListMargin) {
    return `${html.slice(0, lastParagraphMargin)}margin:0;${html.slice(lastParagraphMargin + 'margin:0 0 16px;'.length)}`;
  }
  return `${html.slice(0, lastListMargin)}margin:0 0 0 24px;${html.slice(lastListMargin + 'margin:0 0 16px 24px;'.length)}`;
}

export function getTextBlockPreviewHtml(value: string, sampleName = 'there'): string {
  const sanitized = sanitizeRichTextHtml(replacePreviewTokens(value, sampleName));
  if (sanitized) return sanitized;
  return '<p>Add your copy here.</p>';
}

export function getStyledTextBlockPreviewHtml(
  value: string,
  options?: {
    sampleName?: string;
    align?: 'left' | 'center' | 'right';
    fontFamily?: string | null;
    fontSize?: number | null;
    lineHeight?: number | null;
    /** Text modules render line groups tightly; use spacer or padding controls for deliberate vertical gaps. */
    compact?: boolean;
  }
): string {
  const baseHtml = getTextBlockPreviewHtml(value, options?.sampleName);
  const align = options?.align || 'left';
  const fontFamily = sanitizeRichTextFontFamily(options?.fontFamily || '') || '';
  const fontSize = Math.max(options?.fontSize ?? 16, 10);
  const lineHeight = options?.lineHeight ?? 1.7;
  const pMargin = '0';
  const lMargin = '0 0 0 24px';
  const paragraphStyle = `margin:${pMargin};color:inherit;font-size:${fontSize}px;line-height:${lineHeight};text-align:${align};${fontFamily ? `font-family:${fontFamily};` : ''}`;
  const listStyle = `margin:${lMargin};padding:0;color:inherit;font-size:${fontSize}px;line-height:${lineHeight};text-align:${align};${fontFamily ? `font-family:${fontFamily};` : ''}`;

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return trimLastTextBlockMargin(baseHtml
      .replace(/<p>/gi, `<p style="${paragraphStyle}">`)
      .replace(/<div>/gi, `<p style="${paragraphStyle}">`)
      .replace(/<\/div>/gi, '</p>')
      .replace(/<ul>/gi, `<ul style="${listStyle}">`)
      .replace(/<ol>/gi, `<ol style="${listStyle}">`)
      .replace(/<li>/gi, '<li style="margin:0 0 8px;">')
      .replace(/<a /gi, '<a style="color:inherit;text-decoration:underline;" '));
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${baseHtml}</div>`, 'text/html');

  const serializeNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const children = Array.from(element.childNodes).map(serializeNode).join('');

    if (tag === 'br') return '<br />';
    if (tag === 'strong' || tag === 'b') return `<strong>${children}</strong>`;
    if (tag === 'em' || tag === 'i') return `<em>${children}</em>`;
    if (tag === 'u') return `<span style="text-decoration:underline;">${children}</span>`;
    if (tag === 'p' || tag === 'div') {
      const existingStyle = element.getAttribute('style') || '';
      const mergedStyle = existingStyle ? `${paragraphStyle}${existingStyle}` : paragraphStyle;
      return `<p style="${mergedStyle}">${children}</p>`;
    }
    if (tag === 'ul' || tag === 'ol') return `<${tag} style="${listStyle}">${children}</${tag}>`;
    if (tag === 'li') return `<li style="margin:0 0 8px;">${children}</li>`;
    if (tag === 'a') {
      const href = sanitizeRichTextHref(element.getAttribute('href'));
      return href
        ? `<a href="${escapeHtml(href)}" style="color:inherit;text-decoration:underline;">${children}</a>`
        : children;
    }
    if (tag === 'span' || tag === 'font') return children;

    return children;
  };

  const nodes = Array.from(doc.body.firstChild?.childNodes || []);
  return trimLastTextBlockMargin(nodes.map(serializeNode).join(''));
}

export function plainTextToRichTextHtml(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  return trimmed
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

export function richTextHtmlToPlainText(value: string): string {
  const sanitized = sanitizeRichTextHtml(value);
  if (!sanitized) return '';

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return Object.entries(HTML_ENTITY_MAP)
      .reduce(
        (nextValue, [entity, replacement]) => nextValue.replace(new RegExp(entity, 'g'), replacement),
        sanitized
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>\s*<p>/gi, '\n\n')
          .replace(/<\/li>\s*<li>/gi, '\n')
          .replace(/<[^>]+>/g, '')
      )
      .trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sanitized}</div>`, 'text/html');
  const lines: string[] = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      lines.push(node.textContent || '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();

    if (tag === 'br') {
      lines.push('\n');
      return;
    }

    if (tag === 'li') {
      lines.push('• ');
    }

    Array.from(element.childNodes).forEach(walk);

    if (tag === 'p' || tag === 'div') {
      lines.push('\n\n');
    } else if (tag === 'li') {
      lines.push('\n');
    } else if (tag === 'ul' || tag === 'ol') {
      lines.push('\n');
    }
  };

  Array.from(doc.body.firstChild?.childNodes || []).forEach(walk);
  return lines.join('').replace(/\n{3,}/g, '\n\n').trim();
}
